import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Pencil, Trash2, X, Check, Settings } from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

/**
 * Reusable Product Type CRUD panel.
 * Props:
 *  - apiBase: string e.g. `${API_BASE_URL}` (advanced) or `${BACKEND_BASE_URL}/api/basic` (basic)
 *  - productTypes: array
 *  - canEdit: boolean (admin?)
 *  - onChanged: () => Promise<void>  refresh callback
 *  - selectedPT: currently selected PT id (so we can update if it got renamed/deleted)
 *  - onSelectedChange: (newId) => void
 *  - modeLabel: "BASIC" or "ADVANCED" (for headings)
 */
export default function ProductTypeManager({
  apiBase, productTypes, canEdit, onChanged, selectedPT, onSelectedChange, modeLabel = "ADVANCED",
}) {
  const [newPT, setNewPT] = useState({ id: "", name: "", description: "" });
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState({ id: "", name: "", description: "" });
  const [deleteTarget, setDeleteTarget] = useState(null);

  const handleAdd = async () => {
    if (!newPT.id.trim() || !newPT.name.trim()) {
      toast.error("ID and Name are required");
      return;
    }
    const slug = newPT.id.trim().toLowerCase().replace(/\s+/g, "_").replace(/[^a-z0-9_]/g, "");
    if (!slug) {
      toast.error("ID contains no valid characters");
      return;
    }
    setAdding(true);
    try {
      await axios.post(`${apiBase}/product-types`, {
        id: slug,
        name: newPT.name.trim(),
        description: newPT.description.trim(),
      });
      toast.success(`Product "${newPT.name}" added`);
      setNewPT({ id: "", name: "", description: "" });
      await onChanged?.();
      onSelectedChange?.(slug);
    } catch (e) {
      const msg = e.response?.data?.detail || e.message || "Failed to add product";
      toast.error(msg);
    } finally {
      setAdding(false);
    }
  };

  const startEdit = (pt) => {
    setEditingId(pt.id);
    setEditDraft({ id: pt.id, name: pt.name || "", description: pt.description || "" });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditDraft({ id: "", name: "", description: "" });
  };

  const saveEdit = async (originalId) => {
    const newId = editDraft.id.trim().toLowerCase().replace(/\s+/g, "_").replace(/[^a-z0-9_]/g, "");
    if (!newId || !editDraft.name.trim()) {
      toast.error("ID and Name are required");
      return;
    }
    try {
      const payload = {};
      if (newId !== originalId) payload.new_id = newId;
      payload.name = editDraft.name.trim();
      payload.description = editDraft.description.trim();
      const res = await axios.put(`${apiBase}/product-types/${originalId}`, payload);
      toast.success("Product updated");
      cancelEdit();
      await onChanged?.();
      // Update selected PT if it was the one renamed
      if (selectedPT === originalId && res.data?.id) {
        onSelectedChange?.(res.data.id);
      }
    } catch (e) {
      const msg = e.response?.data?.detail || e.message || "Failed to update";
      toast.error(msg);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    const id = deleteTarget.id;
    try {
      await axios.delete(`${apiBase}/product-types/${id}`);
      toast.success(`Product "${deleteTarget.name}" deleted`);
      setDeleteTarget(null);
      await onChanged?.();
      // If the deleted PT was selected, pick the next one (or none)
      if (selectedPT === id) {
        const remaining = (productTypes || []).filter(p => p.id !== id);
        onSelectedChange?.(remaining[0]?.id || "");
      }
    } catch (e) {
      const msg = e.response?.data?.detail || e.message || "Delete failed";
      toast.error(msg);
    }
  };

  return (
    <Card className="border-[#E0E6E1] bg-white" data-testid={`pt-manager-${modeLabel.toLowerCase()}`}>
      <CardHeader className="pb-3">
        <CardTitle className="text-base font-medium text-[#1B291D] flex items-center gap-2" style={{ fontFamily: "Work Sans" }}>
          <Settings className="w-4 h-4 text-[#43A047]" />
          Manage Products
          <span className="text-[10px] text-[#617364] font-normal ml-1">
            ({productTypes?.length || 0} total)
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Add new product */}
        <div className="border border-dashed border-[#43A047]/40 rounded-md p-3 bg-[#F4FAF5]">
          <div className="text-[10px] uppercase tracking-wider font-bold text-[#388E3C] mb-2">Add a new product</div>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-2 items-end">
            <div className="md:col-span-3">
              <Label className="text-[10px] uppercase tracking-wider text-[#617364] font-bold">ID (slug)</Label>
              <Input
                value={newPT.id}
                onChange={e => setNewPT(p => ({ ...p, id: e.target.value }))}
                placeholder="e.g. pet_bottle_1l"
                disabled={!canEdit}
                className="border-[#E0E6E1] mt-1 h-8 text-sm"
                data-testid={`pt-new-id-${modeLabel.toLowerCase()}`}
              />
            </div>
            <div className="md:col-span-3">
              <Label className="text-[10px] uppercase tracking-wider text-[#617364] font-bold">Name</Label>
              <Input
                value={newPT.name}
                onChange={e => setNewPT(p => ({ ...p, name: e.target.value }))}
                placeholder="e.g. 1L PET Bottle"
                disabled={!canEdit}
                className="border-[#E0E6E1] mt-1 h-8 text-sm"
                data-testid={`pt-new-name-${modeLabel.toLowerCase()}`}
              />
            </div>
            <div className="md:col-span-4">
              <Label className="text-[10px] uppercase tracking-wider text-[#617364] font-bold">Description (optional)</Label>
              <Input
                value={newPT.description}
                onChange={e => setNewPT(p => ({ ...p, description: e.target.value }))}
                placeholder="short context"
                disabled={!canEdit}
                className="border-[#E0E6E1] mt-1 h-8 text-sm"
                data-testid={`pt-new-desc-${modeLabel.toLowerCase()}`}
              />
            </div>
            <div className="md:col-span-2">
              <Button
                onClick={handleAdd}
                disabled={!canEdit || adding}
                className="bg-[#43A047] hover:bg-[#388E3C] text-white w-full h-8 text-sm disabled:opacity-50"
                title={!canEdit ? "Admin access required" : ""}
                data-testid={`pt-add-btn-${modeLabel.toLowerCase()}`}
              >
                <Plus className="w-3.5 h-3.5 mr-1" /> Add
              </Button>
            </div>
          </div>
        </div>

        {/* Existing products list */}
        <div>
          <div className="text-[10px] uppercase tracking-wider font-bold text-[#617364] mb-2">Existing products</div>
          {(productTypes?.length || 0) === 0 ? (
            <div className="text-xs text-[#617364] italic p-3 border border-[#E0E6E1] rounded bg-[#F8F9F8]">
              No products yet. Add your first product above.
            </div>
          ) : (
            <div className="border border-[#E0E6E1] rounded">
              <Table>
                <TableHeader>
                  <TableRow className="border-[#E0E6E1] bg-[#F8F9F8]">
                    <TableHead className="text-[10px] uppercase tracking-wider text-[#617364] font-bold w-[180px]">ID</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-wider text-[#617364] font-bold">Name</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-wider text-[#617364] font-bold">Description</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-wider text-[#617364] font-bold w-[150px] text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {productTypes.map(pt => {
                    const editing = editingId === pt.id;
                    const isActive = selectedPT === pt.id;
                    return (
                      <TableRow
                        key={pt.id}
                        className={`border-[#E0E6E1] ${isActive ? "bg-[#F4FAF5]" : ""}`}
                        data-testid={`pt-row-${modeLabel.toLowerCase()}-${pt.id}`}
                      >
                        <TableCell className="text-xs font-mono text-[#1B291D]">
                          {editing ? (
                            <Input
                              value={editDraft.id}
                              onChange={e => setEditDraft(d => ({ ...d, id: e.target.value }))}
                              className="border-[#E0E6E1] h-7 text-xs font-mono"
                              data-testid={`pt-edit-id-${modeLabel.toLowerCase()}-${pt.id}`}
                            />
                          ) : (
                            <span className="flex items-center gap-1">
                              {isActive && <span className="w-1.5 h-1.5 rounded-full bg-[#43A047]" />}
                              {pt.id}
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="text-xs text-[#1B291D]">
                          {editing ? (
                            <Input
                              value={editDraft.name}
                              onChange={e => setEditDraft(d => ({ ...d, name: e.target.value }))}
                              className="border-[#E0E6E1] h-7 text-xs"
                              data-testid={`pt-edit-name-${modeLabel.toLowerCase()}-${pt.id}`}
                            />
                          ) : (
                            <span className="font-medium">{pt.name}</span>
                          )}
                        </TableCell>
                        <TableCell className="text-xs text-[#617364]">
                          {editing ? (
                            <Input
                              value={editDraft.description}
                              onChange={e => setEditDraft(d => ({ ...d, description: e.target.value }))}
                              className="border-[#E0E6E1] h-7 text-xs"
                              data-testid={`pt-edit-desc-${modeLabel.toLowerCase()}-${pt.id}`}
                            />
                          ) : (
                            <span className="italic">{pt.description || "—"}</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          {editing ? (
                            <div className="flex gap-1 justify-end">
                              <Button
                                size="sm"
                                onClick={() => saveEdit(pt.id)}
                                className="h-7 px-2 bg-[#43A047] hover:bg-[#388E3C] text-white"
                                data-testid={`pt-save-${modeLabel.toLowerCase()}-${pt.id}`}
                              >
                                <Check className="w-3 h-3" />
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={cancelEdit}
                                className="h-7 px-2 border-[#E0E6E1] text-[#617364]"
                                data-testid={`pt-cancel-${modeLabel.toLowerCase()}-${pt.id}`}
                              >
                                <X className="w-3 h-3" />
                              </Button>
                            </div>
                          ) : (
                            <div className="flex gap-1 justify-end">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => onSelectedChange?.(pt.id)}
                                className="h-7 px-2 border-[#E0E6E1] text-[#43A047] text-[10px]"
                                disabled={isActive}
                                title={isActive ? "Currently selected" : "Open benchmarks"}
                                data-testid={`pt-open-${modeLabel.toLowerCase()}-${pt.id}`}
                              >
                                {isActive ? "Active" : "Open"}
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => startEdit(pt)}
                                disabled={!canEdit}
                                className="h-7 px-2 border-[#E0E6E1] text-[#617364] disabled:opacity-50"
                                title={!canEdit ? "Admin access required" : "Edit name/ID"}
                                data-testid={`pt-edit-${modeLabel.toLowerCase()}-${pt.id}`}
                              >
                                <Pencil className="w-3 h-3" />
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => setDeleteTarget(pt)}
                                disabled={!canEdit}
                                className="h-7 px-2 border-red-200 text-red-600 hover:bg-red-50 disabled:opacity-50"
                                title={!canEdit ? "Admin access required" : "Delete product"}
                                data-testid={`pt-delete-${modeLabel.toLowerCase()}-${pt.id}`}
                              >
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            </div>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </div>

        {/* Delete confirmation dialog */}
        <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete this product?</AlertDialogTitle>
              <AlertDialogDescription>
                You are about to delete <span className="font-semibold text-[#1B291D]">{deleteTarget?.name}</span>{" "}
                <span className="font-mono text-xs">({deleteTarget?.id})</span>.
                <br />
                <br />
                Its benchmark rows will be kept in the database (as orphans) but the product itself
                will no longer appear in any list. This cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="pt-delete-cancel">Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={confirmDelete}
                className="bg-red-600 hover:bg-red-700 text-white"
                data-testid="pt-delete-confirm"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </CardContent>
    </Card>
  );
}
